/**
 * Single source of truth for the admin permission-role checkboxes shown across
 * the system and organization admin forms (add + details pages).
 *
 * Each field maps a legacy bitwise flag (see `ADMIN_PERMISSIONS` in constants)
 * and GraphQL role (see `ADMIN_FLAG_TO_ROLE_NAME`) to its display label and
 * description. Edit labels/descriptions/order here to change them everywhere.
 */

export type PermissionFlag =
  | 'admin'
  | 'users'
  | 'rental'
  | 'physical'
  | 'locations'
  | 'memberships'
  | 'support'
  | 'maintenance'
  | 'rates'
  | 'refunds'
  | 'bank';

export type PermissionFlags = Record<PermissionFlag, boolean>;

export interface PermissionField {
  name: PermissionFlag;
  label: string;
  description: string;
}

export const PERMISSION_FIELDS: PermissionField[] = [
  { name: 'admin', label: 'Admin', description: 'Permission to manage admins' },
  { name: 'users', label: 'Users', description: 'Permission to manage users' },
  {
    name: 'rental',
    label: 'Rentals',
    description: 'Permission to manage rentals',
  },
  {
    name: 'physical',
    label: 'Hardware',
    description: 'Permission to manage hardware',
  },
  {
    name: 'locations',
    label: 'Locations',
    description: 'Permission to manage locations',
  },
  {
    name: 'memberships',
    label: 'Memberships',
    description: 'Permission to manage memberships',
  },
  {
    name: 'support',
    label: 'Support Tickets',
    description: 'Permission to manage support tickets',
  },
  {
    name: 'maintenance',
    label: 'Maintenance',
    description: 'Permission to manage maintenance',
  },
  { name: 'rates', label: 'Rates', description: 'Permission to manage rates' },
  {
    name: 'refunds',
    label: 'Refunds',
    description: 'Permission to manage refunds',
  },
  {
    name: 'bank',
    label: 'Settings',
    description: 'Permission to manage settings',
  },
];

/** A `PermissionFlags` record with every flag set to the given value. */
export const buildPermissionFlags = (value: boolean): PermissionFlags =>
  PERMISSION_FIELDS.reduce(
    (acc, field) => ({ ...acc, [field.name]: value }),
    {} as PermissionFlags
  );

/** A `PermissionFlags` record with every flag set to `false`. */
export const emptyPermissionFlags = (): PermissionFlags => buildPermissionFlags(false);
